namespace RS
{
    export type IAwaitable<T> = PromiseLike<T>;

    export type AwaitableValue<T> = IAwaitable<T> | T;

    /**
     * Encapsulates an object that can be awaited on.
     */
    export abstract class Awaitable<TResult = void, TRejectionReason = any> implements IAwaitable<TResult>
    {
        private __awaitableCallbacks: RS.PendingPromise[] | null = null;
        private __awaitableResult: Result<TResult, any> | null = null;

        /**
         * Attaches callbacks for the resolution and/or rejection of the Promise.
         * @param onfulfilled The callback to execute when the Promise is resolved.
         * @param onrejected The callback to execute when the Promise is rejected.
         * @returns A Promise for the completion of which ever callback is executed.
         */
        public then<TResult1 = TResult, TResult2 = never>(onfulfilled?: ((value: TResult) => TResult1 | PromiseLike<TResult1>) | undefined | null, onrejected?: ((reason: any) => TResult2 | PromiseLike<TResult2>) | undefined | null): PromiseLike<TResult1 | TResult2>
        {
            return new Promise((resolve, reject) =>
            {
                this.thenInternal((data) =>
                {
                    if (onfulfilled)
                    {
                        try
                        {
                            resolve(onfulfilled(data));
                        }
                        catch (err)
                        {
                            reject(err);
                        }
                    }
                    else
                    {
                        resolve();
                    }
                }, (reason) =>
                {
                    if (onrejected)
                    {
                        try
                        {
                            return resolve(onrejected(reason));
                        }
                        catch (err)
                        {
                            return reject(err);
                        }
                    }
                    else
                    {
                        return reject(reason);
                    }
                });
            });
        }

        protected resolve(result?: TResult): void
        {
            if (this.__awaitableResult) { return; }
            if (this.__awaitableCallbacks)
            {
                for (const promiseCallback of this.__awaitableCallbacks)
                {
                    if (promiseCallback.resolve)
                    {
                        promiseCallback.resolve(result);
                    }
                }
                this.__awaitableCallbacks = null;
            }

            this.__awaitableResult = { type: "resolution", result };
        }

        protected reject(reason?: TRejectionReason): void
        {
            if (this.__awaitableResult) { return; }
            if (this.__awaitableCallbacks)
            {
                for (const promiseCallback of this.__awaitableCallbacks)
                {
                    if (promiseCallback.reject)
                    {
                        promiseCallback.reject(reason);
                    }
                }
                this.__awaitableCallbacks = null;
            }
            
            this.__awaitableResult = { type: "rejection", reason };
        }

        protected clearAwaitableResult()
        {
            this.__awaitableResult = null;
        }

        private thenInternal(resolve: (data?: TResult) => void, reject: (reason?: TRejectionReason) => void): void
        {
            if (this.__awaitableResult)
            {
                switch (this.__awaitableResult.type)
                {
                    case "rejection":
                    {
                        reject(this.__awaitableResult.reason);
                        return;
                    }
                    case "resolution":
                    {
                        resolve(this.__awaitableResult.result);
                        return;
                    }
                }
            }
            if (!this.__awaitableCallbacks) { this.__awaitableCallbacks = []; }
            this.__awaitableCallbacks.push({ resolve, reject });
        }
    }

    interface Rejection<T> { type: "rejection"; reason?: T; }
    interface Resolution<T> { type: "resolution"; result?: T; }
    type Result<TResolvedResult, TRejectionReason> = Resolution<TResolvedResult> | Rejection<TRejectionReason>;
}